import { Plugins } from '@capacitor/core';

import LocalStorage from 'utils/cognito/storage';
import { env } from 'utils/environment';
import { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';

import { AttributeName, TraceAttribute, TraceName } from './types';

const { FirebasePerformancePlugin } = Plugins;

export const startTrace = async (traceName: TraceName) => {
  try {
    await FirebasePerformancePlugin?.startTrace?.({ traceName });
  } catch (error) {
    logger.error({ error, traceName, message: 'Performance.startTrace error' });
  }
};

export const stopTrace = async (traceName: TraceName) => {
  try {
    await setDefaultAttributes(traceName);
    await FirebasePerformancePlugin?.stopTrace?.({ traceName });
  } catch (error) {
    logger.error({ error, traceName, message: 'Performance.stopTrace error' });
  }
};

// Get a trace attribute value or return null if it failed or attribute did not exist
export const getAttribute = async (traceName: TraceName, attributeName: AttributeName) => {
  try {
    return await FirebasePerformancePlugin?.getTraceAttribute?.({
      traceName,
      attributeName,
    });
  } catch (error) {
    logger.error({
      error,
      traceName,
      attributeName,
      message: 'Performance.getAttribute error',
    });
  }

  return null;
};

// Asynchronously set the attribute on a performance trace
// *Note* you should await on setAttribute before calling stopTrace otherwise you risk
// stopping the trace without setting the attributes
export const setAttribute = async (
  traceName: TraceName,
  attributeName: AttributeName,
  attributeValue: string
) => {
  try {
    await FirebasePerformancePlugin?.setTraceAttribute?.({
      traceName,
      attributeName,
      attributeValue,
    });
  } catch (error) {
    logger.error({
      error,
      traceName,
      attributeName,
      attributeValue,
      message: 'Performance.setAttribute error',
    });
  }
};

// Set an array of attributes on a trace
export const setAttributes = async (traceName: TraceName, attributes: TraceAttribute[]) => {
  return Promise.all(
    attributes.map(({ name, value }) => {
      return setAttribute(traceName, name, value);
    })
  );
};

const setDefaultAttributes = async (traceName: TraceName) => {
  await setAttribute(traceName, AttributeName.ENVIRONMENT, env());
  await trySetCognitoIdAttribute(traceName);
};

const trySetCognitoIdAttribute = async (traceName: TraceName) => {
  const cognitoId = LocalStorage?.getItem(StorageKeys.USER)?.cognitoId;
  if (cognitoId) {
    await setAttribute(traceName, AttributeName.COGNITO_ID, cognitoId);
    await setAttribute(traceName, AttributeName.WAS_USER_LOGGED_IN, String(true));
  } else {
    await setAttribute(traceName, AttributeName.WAS_USER_LOGGED_IN, String(false));
  }
};
